<script setup>
import { ref, computed, onMounted } from 'vue';
import { useFunctionGetter, useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import ButtonNext from 'next/button/Button.vue';
import twentyApi from 'dashboard/api/integrations/twenty.js';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const store = useStore();

const integrationLoaded = ref(false);
const webhookUrl = ref('');
const showConnectForm = ref(false);
const isSyncing = ref(false);
const isConnecting = ref(false);
const connectForm = ref({ api_key: '', api_url: 'http://localhost:3003' });

const integration = useFunctionGetter('integrations/getIntegration', 'twenty');
const uiFlags = useMapGetter('integrations/getUIFlags');

const hasConnectedHooks = computed(() => {
  return integration.value && integration.value.hooks && integration.value.hooks.length > 0;
});

const fetchSettings = async () => {
  try {
    const response = await twentyApi.getSettings();
    if (response.data && response.data.webhook_url) {
      webhookUrl.value = response.data.webhook_url;
    }
  } catch (error) {
    // Ignore error if not connected yet
  }
};

const initializeTwentyIntegration = async () => {
  await store.dispatch('integrations/get');
  await fetchSettings();
  integrationLoaded.value = true;
};

const copyWebhookUrl = () => {
  if (!webhookUrl.value) return;
  copyTextToClipboard(webhookUrl.value);
  useAlert(t('INTEGRATION_APPS.LIST.COPY_SUCCESS') || 'آدرس وب‌هوک در حافظه کپی شد');
};

const handleConnect = async () => {
  if (!connectForm.value.api_key || !connectForm.value.api_url) {
    useAlert('لطفاً API Key و API URL را وارد کنید');
    return;
  }
  isConnecting.value = true;
  try {
    await store.dispatch('integrations/createHook', {
      app_id: 'twenty',
      settings: {
        api_key: connectForm.value.api_key,
        api_url: connectForm.value.api_url,
      },
    });
    showConnectForm.value = false;
    connectForm.value = { api_key: '', api_url: 'http://localhost:3003' };
    useAlert('اتصال به Twenty CRM با موفقیت انجام شد');
    await initializeTwentyIntegration();
  } catch (error) {
    useAlert('خطا در اتصال به Twenty CRM. لطفاً API Key و URL را بررسی کنید.');
  } finally {
    isConnecting.value = false;
  }
};

const handleDisconnect = async () => {
  if (!hasConnectedHooks.value) return;
  try {
    const hook = integration.value.hooks[0];
    await store.dispatch('integrations/deleteHook', {
      hookId: hook.id,
      appId: hook.app_id,
    });
    webhookUrl.value = '';
    useAlert('اتصال Twenty CRM با موفقیت قطع شد');
    initializeTwentyIntegration();
  } catch (error) {
    useAlert('خطا در قطع اتصال Twenty CRM');
  }
};

const triggerBulkSync = async () => {
  isSyncing.value = true;
  try {
    await twentyApi.bulkSync();
    useAlert('همگام‌سازی کلی مخاطبین در پس‌زمینه آغاز شد.');
  } catch (error) {
    useAlert('خطا در شروع همگام‌سازی کلی مخاطبین.');
  } finally {
    isSyncing.value = false;
  }
};

onMounted(() => {
  initializeTwentyIntegration();
});
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded || uiFlags.isFetching">
    <template #header>
      <BaseSettingsHeader
        title="Twenty CRM"
        description="یکپارچه‌سازی دوطرفه چت‌ووت با سامانه مدیریت ارتباط با مشتری Twenty CRM"
        feature-name="twenty_integration"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="space-y-6">
        <!-- Main Integration Status Card -->
        <div class="outline outline-n-container outline-1 bg-n-card rounded-xl p-6">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-4">
              <div class="flex h-16 w-16 items-center justify-center rounded-xl bg-n-alpha-3 dark:bg-n-alpha-2 border border-n-weak p-2">
                <img
                  :src="'/dashboard/images/integrations/twenty.png'"
                  class="max-w-full block dark:hidden rounded-md"
                />
                <img
                  :src="'/dashboard/images/integrations/twenty-dark.png'"
                  class="max-w-full hidden dark:block rounded-md"
                />
              </div>
              <div>
                <h3 class="text-heading-2 font-semibold text-n-slate-12">Twenty CRM</h3>
                <p class="text-n-slate-11 text-body-main mt-1">
                  یکپارچه‌سازی کامل چت‌ووت با Twenty CRM جهت همگام‌سازی دوطرفه مخاطبین، پروفایل، متن گفتگوها، وظایف و فرصت‌های فروش.
                </p>
              </div>
            </div>
            <div>
              <div v-if="hasConnectedHooks">
                <ButtonNext
                  ruby
                  faded
                  label="قطع اتصال"
                  @click="handleDisconnect"
                />
              </div>
              <div v-else>
                <ButtonNext
                  blue
                  label="اتصال به Twenty CRM"
                  @click="showConnectForm = !showConnectForm"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Connect Form (shown when not connected and button clicked) -->
        <div
          v-if="!hasConnectedHooks && showConnectForm"
          class="outline outline-n-container outline-1 bg-n-card rounded-xl p-6 space-y-4"
        >
          <h4 class="text-heading-3 font-semibold text-n-slate-12">اتصال به Twenty CRM</h4>
          <p class="text-n-slate-11 text-body-sub">
            برای اتصال، کلید API و آدرس نصب Twenty CRM خود را وارد کنید.
          </p>

          <div class="space-y-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">API Key</label>
              <input
                v-model="connectForm.api_key"
                type="text"
                placeholder="eyJhbGciOiJFUzI1NiIs..."
                class="w-full bg-n-alpha-2 border border-n-weak rounded-lg px-4 py-2 text-n-slate-12 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">API URL</label>
              <input
                v-model="connectForm.api_url"
                type="text"
                placeholder="http://localhost:3003"
                class="w-full bg-n-alpha-2 border border-n-weak rounded-lg px-4 py-2 text-n-slate-12 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 pt-2">
            <ButtonNext
              slate
              faded
              label="انصراف"
              @click="showConnectForm = false"
            />
            <ButtonNext
              blue
              :loading="isConnecting"
              label="اتصال"
              @click="handleConnect"
            />
          </div>
        </div>

        <!-- Webhook Information Box when connected -->
        <div v-if="hasConnectedHooks && webhookUrl" class="outline outline-n-container outline-1 bg-n-card rounded-xl p-6 space-y-4">
          <h4 class="text-heading-3 font-semibold text-n-slate-12">آدرس وب‌هوک (Webhook URL)</h4>
          <p class="text-n-slate-11 text-body-sub">
            جهت ارسال تغییرات مخاطبین و اطلاعات از Twenty CRM به چت‌ووت، آدرس وب‌هوک زیر را در Twenty CRM ثبت کنید:
          </p>

          <div class="flex items-center gap-3">
            <input
              type="text"
              readonly
              :value="webhookUrl"
              class="flex-1 bg-n-alpha-2 border border-n-weak rounded-lg px-4 py-2 text-n-slate-12 text-sm font-mono focus:outline-none"
            />
            <ButtonNext
              blue
              faded
              label="کپی آدرس وب‌هوک"
              icon="i-lucide-copy"
              @click="copyWebhookUrl"
            />
          </div>

          <div class="mt-4 pt-4 border-t border-n-weak flex items-center justify-between">
            <div>
              <h5 class="text-sm font-medium text-n-slate-12">همگام‌سازی کلی مخاطبین (Bulk Sync)</h5>
              <p class="text-xs text-n-slate-11 mt-0.5">همگام‌سازی یکباره و گروهی تمام مخاطبان چت‌ووت با Twenty CRM</p>
            </div>
            <ButtonNext
              slate
              outline
              :loading="isSyncing"
              label="همگام‌سازی همگی مخاطبین"
              @click="triggerBulkSync"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
